// skipcq: JS-W1028, JS-0128
import React, { useCallback, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import { SPLAT_TYPES, getSplatTypeLabel } from './splatConstants';
import {
  SPLAT_IMAGE_MAX_BYTES,
  SPLAT_IMAGE_SIZE_PX,
} from './splatImageUpload';

const SPLAT_IMAGE_HINT = `Splat images must be PNG, ${SPLAT_IMAGE_SIZE_PX}×${SPLAT_IMAGE_SIZE_PX} pixels, under ${SPLAT_IMAGE_MAX_BYTES / 1024}KB. Upload them on the Splat Images tab.`;

function noop() {}

function completionSplatOptionLabel(option) {
  return option.name?.trim() || option.id;
}

function completionSplatOptionEqual(a, b) {
  return a.id === b.id;
}

function eventDefinitionOptionLabel(option) {
  if (!option) return '';
  return option.name?.trim() || option.id;
}

function eventDefinitionOptionEqual(a, b) {
  if (!a || !b) return false;
  return a.id === b.id;
}

function renderCompletionSplatInput(params) {
  return (
    <TextField
      {...params}
      label="Required splats"
      placeholder="Search splats…"
      required
    />
  );
}

function AddSplatDialogFields({
  isEdit,
  form,
  onChange,
  availableImages,
  imagesLoading,
  selectValue,
  completionOptions,
  onCompletionSplatIdsChange,
  eventOptions,
  eventsLoading,
  onEventIdChange,
}) {
  const completionValue = useMemo(() => {
    const ids = form.completionSplatIds ?? [];
    const byId = new Map(completionOptions.map((s) => [s.id, s]));
    return ids.map((id) => byId.get(id)).filter(Boolean);
  }, [form.completionSplatIds, completionOptions]);

  const eventPickerValue = useMemo(() => {
    const idValue = String(form.eventId ?? '').trim();
    if (!idValue) return null;
    return eventOptions.find((eventRow) => eventRow.id === idValue) ?? null;
  }, [form.eventId, eventOptions]);

  const handleCompletionChange = useCallback(
    function handleCompletionSelect(_event, newValue) {
      onCompletionSplatIdsChange(newValue.map((s) => s.id));
    },
    [onCompletionSplatIdsChange],
  );

  const renderEventPickerInput = useCallback(
    function renderEventPickerTextField(params) {
      return (
        <TextField
          {...params}
          label="Event"
          placeholder="Search events…"
          required
          helperText={
            eventsLoading
              ? 'Loading events…'
              : !eventOptions.length
                ? 'No event definitions found.'
                : ''
          }
        />
      );
    },
    [eventsLoading, eventOptions.length],
  );

  return (
    <Stack
      spacing={2}
      sx={{
        mt: 1,
      }}
    >
      <TextField
        label="ID (optional; leave blank for auto-generated)"
        name="id"
        value={form.id}
        onChange={onChange}
        fullWidth
        disabled={isEdit}
      />
      <TextField
        label="Name"
        name="name"
        value={form.name}
        onChange={onChange}
        required
        fullWidth
      />
      <FormControl fullWidth>
        <InputLabel id="splat-type-label">Type</InputLabel>
        <Select
          labelId="splat-type-label"
          label="Type"
          name="type"
          value={form.type || 'custom'}
          onChange={onChange}
        >
          {SPLAT_TYPES.map((splatType) => (
            <MenuItem key={splatType} value={splatType}>
              {getSplatTypeLabel(splatType)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {form.type === 'completion' && (
        <Autocomplete
          multiple
          options={completionOptions}
          value={completionValue}
          onChange={handleCompletionChange}
          getOptionLabel={completionSplatOptionLabel}
          isOptionEqualToValue={completionSplatOptionEqual}
          renderInput={renderCompletionSplatInput}
        />
      )}
      {form.type === 'first_event_review' && (
        <Autocomplete
          options={eventOptions}
          value={eventPickerValue}
          onChange={onEventIdChange}
          getOptionLabel={eventDefinitionOptionLabel}
          isOptionEqualToValue={eventDefinitionOptionEqual}
          loading={eventsLoading}
          renderInput={renderEventPickerInput}
        />
      )}
      <TextField
        label="Text"
        name="text"
        value={form.text}
        onChange={onChange}
        required
        fullWidth
        multiline
        maxRows={5}
      />
      <TextField
        label="Overlay text (optional)"
        name="overlayText"
        value={form.overlayText ?? ''}
        onChange={onChange}
        fullWidth
      />
      <TextField
        label="Beer Journey Map (optional)"
        name="beerJourneyMap"
        value={form.beerJourneyMap ?? ''}
        onChange={onChange}
        fullWidth
      />
      {form.type !== 'completion' && form.type !== 'first_event_review' && (
        <TextField
          label="Trigger threshold (optional)"
          name="triggerThreshold"
          type="number"
          value={
            form.triggerThreshold === '' ||
            form.triggerThreshold === null ||
            form.triggerThreshold === undefined
              ? ''
              : form.triggerThreshold
          }
          onChange={onChange}
          fullWidth
          inputProps={{ inputMode: 'numeric' }}
        />
      )}
      <FormControl fullWidth required disabled={!availableImages.length}>
        <InputLabel id="splat-image-label">Image</InputLabel>
        <Select
          labelId="splat-image-label"
          label="Image"
          name="image"
          value={selectValue}
          onChange={onChange}
        >
          {availableImages.map((filename) => (
            <MenuItem key={filename} value={filename}>
              {filename}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {!availableImages.length && !imagesLoading && (
        <Alert severity="warning">
          No images available yet. Open the Splat Images tab and upload at
          least one PNG.
        </Alert>
      )}
      {imagesLoading && (
        <Alert severity="info">Loading image list from Storage…</Alert>
      )}
      <TextField
        label="Limit"
        name="limit"
        type="number"
        value={form.limit}
        onChange={onChange}
        fullWidth
      />
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(form.isActive)}
            name="isActive"
            onChange={onChange}
          />
        }
        label="Active"
      />
    </Stack>
  );
}

function AddSplatDialogContent({
  errorMsg,
  form,
  isEdit,
  onChange,
  availableImages,
  imagesLoading,
  selectValue,
  completionOptions,
  onCompletionSplatIdsChange,
  eventOptions,
  eventsLoading,
  onEventIdChange,
}) {
  return (
    <DialogContent>
      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMsg}
        </Alert>
      )}
      <Alert severity="info" sx={{ mb: 2 }}>
        {SPLAT_IMAGE_HINT}
      </Alert>
      <AddSplatDialogFields
        isEdit={isEdit}
        form={form}
        onChange={onChange}
        availableImages={availableImages}
        imagesLoading={imagesLoading}
        selectValue={selectValue}
        completionOptions={completionOptions}
        onCompletionSplatIdsChange={onCompletionSplatIdsChange}
        eventOptions={eventOptions}
        eventsLoading={eventsLoading}
        onEventIdChange={onEventIdChange}
      />
    </DialogContent>
  );
}

/**
 * AddSplatDialog presents a form to create or edit a splat.
 */
function AddSplatDialog({
  open,
  mode,
  submitting,
  errorMsg,
  form,
  allSplats = [],
  excludeSplatId = '',
  availableImages = [],
  imagesLoading = false,
  eventOptions = [],
  eventsLoading = false,
  validEventIdSet = new Set(),
  onClose,
  onChange,
  onCompletionSplatIdsChange = noop,
  onEventIdChange = noop,
  onSubmit,
}) {
  const isEdit = mode === 'edit';
  const trimmedImage = (form.image ?? '').trim();
  const imageOk =
    Boolean(trimmedImage) && availableImages.includes(trimmedImage);
  const selectValue = imageOk
    ? trimmedImage
    : (availableImages[0] ?? '');

  const completionOptions = useMemo(
    () =>
      allSplats.filter(
        (s) =>
          s.type !== 'completion' &&
          s.id &&
          (!excludeSplatId || s.id !== excludeSplatId),
      ),
    [allSplats, excludeSplatId],
  );

  const completionOptionIdSet = useMemo(
    () =>
      new Set(
        completionOptions
          .map((s) => String(s.id ?? '').trim())
          .filter((id) => id.length > 0),
      ),
    [completionOptions],
  );

  const completionOk =
    form.type !== 'completion' ||
    (Array.isArray(form.completionSplatIds) &&
      form.completionSplatIds.some(
        (id) =>
          id != null &&
          completionOptionIdSet.has(String(id).trim()),
      ));

  const firstEventReviewSelectedId = String(form.eventId ?? '').trim();
  const firstEventReviewOk =
    form.type !== 'first_event_review' ||
    (!eventsLoading &&
      firstEventReviewSelectedId.length > 0 &&
      validEventIdSet.has(firstEventReviewSelectedId));

  const canSubmit =
    Boolean(form.name?.trim()) &&
    Boolean(form.text?.trim()) &&
    imageOk &&
    completionOk &&
    firstEventReviewOk &&
    !submitting;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Edit Splat' : 'Add Splat'}</DialogTitle>
      <AddSplatDialogContent
        errorMsg={errorMsg}
        form={form}
        isEdit={isEdit}
        onChange={onChange}
        availableImages={availableImages}
        imagesLoading={imagesLoading}
        selectValue={selectValue}
        completionOptions={completionOptions}
        onCompletionSplatIdsChange={onCompletionSplatIdsChange}
        eventOptions={eventOptions}
        eventsLoading={eventsLoading}
        onEventIdChange={onEventIdChange}
      />
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={!canSubmit}
          variant="contained"
        >
          {submitting
            ? isEdit
              ? 'Saving…'
              : 'Creating…'
            : isEdit
              ? 'Save'
              : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddSplatDialog;
